import React, { useState } from 'react';
import { 
  FaPlus, FaEdit, FaTrash, FaCheck, 
  FaCalendarAlt, FaClock, FaLock, FaFileInvoiceDollar,
  FaCopy, FaSyncAlt, FaChevronLeft, FaChevronRight,
  FaSortAmountDown, FaSortAmountUp,
  FaSearch, FaTimes, FaBolt, FaFilter,
  FaExclamationCircle, FaExclamationTriangle
} from 'react-icons/fa';
import { calculateThreeTuesdays } from './TuesdayInstallmentModal';
import { 
  sortBillsByDueDate, 
  getTodayDateStr, 
  parseDateParts, 
  getYearMonth, 
  formatShortMonth,
  formatMonthYear 
} from './billTrackerData';

const ExpensesSheet = ({ 
  expenses, 
  setExpenses, 
  config, 
  setConfig,
  onOpenTuesdayPlan 
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [extensionModalBill, setExtensionModalBill] = useState(null);
  
  // Month filter: 'current' (active reference month + carried overdue) or 'overdue-only' or 'all' or 'next-monday' or 'urgent-48h'
  const [monthFilter, setMonthFilter] = useState('current');
  const [notificationMessage, setNotificationMessage] = useState('');

  // Instant Search & Category Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'Business' | 'Household' | 'Others'

  // Due date sorting order: 'asc' (earliest first, default) or 'desc' (latest first)
  const [dueDateSortOrder, setDueDateSortOrder] = useState('asc');

  // Extension form state
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  
  // Add/Edit bill form state
  const [formData, setFormData] = useState({
    description: '',
    subDescription: '',
    category: 'Business',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: config.referenceDate || getTodayDateStr(),
    amount: '',
    hasThreeWeekPlan: true,
    repeatNextMonth: false
  });

  const [dateChangeBlockedMessage, setDateChangeBlockedMessage] = useState('');

  // Current reference month metrics parsed safely without timezone offset
  const { year: refYear, month: refMonth, day: refDay } = parseDateParts(config?.referenceDate || getTodayDateStr());
  const refDateObj = new Date(refYear, refMonth - 1, refDay);
  const currentMonthLabel = refDateObj.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Priority color styling helper
  const getPriorityBadgeClass = (priority) => {
    if (priority.startsWith('Important')) return 'bt-badge-important';
    if (priority.startsWith('Average')) return 'bt-badge-average';
    return 'bt-badge-less';
  };

  const getCategoryBadgeClass = (cat) => {
    if (cat === 'Business') return 'bt-badge-business';
    if (cat === 'Household') return 'bt-badge-household';
    return 'bt-badge-others';
  };

  // Helper to determine if an unpaid bill is carried over from an earlier month
  const isBillOverdueCarryOver = (bill) => {
    if (bill.isPaid) return false;
    const bParts = parseDateParts(bill.dueDate);
    return bParts.year < refYear || (bParts.year === refYear && bParts.month < refMonth);
  };

  // Due date status evaluation
  const getDueDateStatus = (bill) => {
    if (bill.isPaid) return { status: 'paid', label: 'Paid', color: '#10b981' };
    if (bill.isExtended) return { status: 'extended', label: 'EXTENDED', color: '#d97706' };

    const bParts = parseDateParts(bill.dueDate);
    const rParts = parseDateParts(config?.referenceDate || getTodayDateStr());
    const bDate = new Date(bParts.year, bParts.month - 1, bParts.day);
    const rDate = new Date(rParts.year, rParts.month - 1, rParts.day);

    if (bDate < rDate) {
      const isCarry = bParts.year < rParts.year || (bParts.year === rParts.year && bParts.month < rParts.month);
      return { 
        status: 'overdue', 
        label: isCarry ? 'OVERDUE (CARRY-OVER)' : 'OVERDUE', 
        color: '#dc2626',
        isCarry
      };
    }
    return { status: 'normal', label: 'ON TRACK', color: '#10b981' };
  };

  // Toggle paid status
  const handleTogglePaid = (id) => {
    setExpenses(prev => prev.map(bill => {
      if (bill.id === id) {
        const nextPaid = !bill.isPaid;
        // Also update installments if any
        const updatedInstallments = bill.installments
          ? bill.installments.map(inst => ({ ...inst, isPaid: nextPaid }))
          : bill.installments;
        return { ...bill, isPaid: nextPaid, installments: updatedInstallments };
      }
      return bill;
    }));
  };

  // Open add modal
  const handleOpenAdd = () => {
    setFormData({
      description: '',
      subDescription: '',
      category: 'Business',
      isInvoice: true,
      priority: 'Important (High Priority)',
      dueDate: config.referenceDate,
      amount: '',
      hasThreeWeekPlan: true,
      repeatNextMonth: false
    });
    setEditingBill(null);
    setDateChangeBlockedMessage('');
    setIsAddModalOpen(true);
  };

  // Open edit modal
  const handleOpenEdit = (bill) => {
    setEditingBill(bill);
    setFormData({
      description: bill.description,
      subDescription: bill.subDescription || '',
      category: bill.category,
      isInvoice: bill.isInvoice,
      priority: bill.priority,
      dueDate: bill.dueDate,
      amount: bill.amount,
      hasThreeWeekPlan: bill.hasThreeWeekPlan,
      repeatNextMonth: bill.repeatNextMonth || false
    });
    setDateChangeBlockedMessage('');
    setIsAddModalOpen(true);
  };

  // Duplicate an existing bill
  const handleDuplicateBill = (billToClone) => {
    const newId = `exp-${Date.now()}`;
    const amountNum = parseFloat(billToClone.amount) || 0;
    const hasPlan = billToClone.priority.startsWith('Important') ? true : billToClone.hasThreeWeekPlan;
    const installments = hasPlan
      ? calculateThreeTuesdays(billToClone.dueDate, amountNum)
      : [];

    const duplicatedBill = {
      ...billToClone,
      id: newId,
      description: `${billToClone.description} (Copy)`,
      amount: amountNum,
      isPaid: false,
      isExtended: false,
      extensionDate: '',
      extensionReason: '',
      hasThreeWeekPlan: hasPlan,
      installments,
      repeatNextMonth: billToClone.repeatNextMonth || false
    };

    setExpenses(prev => {
      const nextList = [duplicatedBill, ...prev];
      return sortBillsByDueDate(nextList, dueDateSortOrder);
    });

    setNotificationMessage(`Duplicated: "${billToClone.description}" added in order of due date!`);
    setTimeout(() => setNotificationMessage(''), 3500);
  };

  // Shift Reference Month (starts from 1st day of month)
  const handleShiftMonth = (offset) => {
    const nextDateObj = new Date(refYear, refMonth - 1 + offset, 1);
    const nextYear = nextDateObj.getFullYear();
    const nextMonth = String(nextDateObj.getMonth() + 1).padStart(2, '0');
    const newRefDate = `${nextYear}-${nextMonth}-01`;
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: newRefDate,
        autoSyncRealDate: false
      }));
    }
  };

  // Return to today's real calendar date
  const handleReturnToToday = () => {
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: getTodayDateStr(),
        autoSyncRealDate: true
      }));
    }
  };

  // Delete bill
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this expense record?')) {
      setExpenses(prev => prev.filter(b => b.id !== id));
    }
  };

  // Handle due date change with importance restriction
  const handleDueDateInputChange = (newDate) => {
    if (editingBill && editingBill.priority.startsWith('Important')) {
      // Prompt rule: Important bills cannot change due date casually
      setDateChangeBlockedMessage(
        'Contractual Lock: High-priority bills cannot have their due date edited directly. Please use "Request Extension" instead.'
      );
      return;
    }
    setDateChangeBlockedMessage('');
    setFormData(prev => ({ ...prev, dueDate: newDate }));
  };

  // Save new or edited bill
  const handleSaveBill = (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) return;

    const amountNum = parseFloat(formData.amount);
    const hasPlan = formData.priority.startsWith('Important') ? true : formData.hasThreeWeekPlan;

    if (editingBill) {
      setExpenses(prev => {
        const updated = prev.map(bill => {
          if (bill.id === editingBill.id) {
            const installments = hasPlan
              ? calculateThreeTuesdays(formData.dueDate, amountNum)
              : [];
            return {
              ...bill,
              ...formData,
              amount: amountNum,
              hasThreeWeekPlan: hasPlan,
              installments,
              repeatNextMonth: formData.repeatNextMonth
            };
          }
          return bill;
        });
        return sortBillsByDueDate(updated, dueDateSortOrder);
      });
      setNotificationMessage(`Updated: "${formData.description}" saved.`);
      setTimeout(() => setNotificationMessage(''), 3000);
    } else {
      const newId = `exp-${Date.now()}`;
      const installments = hasPlan
        ? calculateThreeTuesdays(formData.dueDate, amountNum)
        : [];
      const newBill = {
        id: newId,
        ...formData,
        amount: amountNum,
        isPaid: false,
        isExtended: false,
        extensionDate: '',
        extensionReason: '',
        hasThreeWeekPlan: hasPlan,
        installments,
        repeatNextMonth: formData.repeatNextMonth
      };
      setExpenses(prev => sortBillsByDueDate([newBill, ...prev], dueDateSortOrder));
      setNotificationMessage(`Added: "${formData.description}" scheduled.`);
      setTimeout(() => setNotificationMessage(''), 3000);
    }

    setIsAddModalOpen(false);
  };

  // Submit Extension Request
  const handleRequestExtension = (e) => {
    e.preventDefault();
    if (!extensionModalBill || !extensionDate) return;

    setExpenses(prev => {
      const updated = prev.map(bill => {
        if (bill.id === extensionModalBill.id) {
          return {
            ...bill,
            dueDate: extensionDate,
            isExtended: true,
            extensionDate: extensionDate,
            extensionReason: extensionReason || 'Extension requested by payer'
          };
        }
        return bill;
      });
      return sortBillsByDueDate(updated, dueDateSortOrder);
    });

    setExtensionModalBill(null);
    setExtensionDate('');
    setExtensionReason('');
  };

  // Proximity Helper: checks if a bill is due within 48 hours relative to referenceDate
  const getProximityStatus = (dueDateStr) => {
    if (!dueDateStr || !config.referenceDate) return null;
    const [bY, bM, bD] = dueDateStr.split('-').map(Number);
    const [rY, rM, rD] = config.referenceDate.split('-').map(Number);
    const bDate = new Date(bY, bM - 1, bD);
    const rDate = new Date(rY, rM - 1, rD);
    const diffDays = Math.round((bDate - rDate) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { isOverdue: true, label: `Overdue (${Math.abs(diffDays)}d)`, level: 'danger', days: diffDays };
    if (diffDays === 0) return { isDueSoon: true, label: '⚡ Due Today', level: 'urgent', days: 0 };
    if (diffDays === 1) return { isDueSoon: true, label: '⚡ Due Tomorrow', level: 'urgent', days: 1 };
    if (diffDays === 2) return { isDueSoon: true, label: '⚡ Due in 48h', level: 'warning', days: 2 };
    return null;
  };

  // Next Monday calculation for filter
  const dayOfWeek = refDateObj.getDay();
  const daysUntilNextMonday = ((1 - dayOfWeek + 6) % 7) + 1;
  const nextMondayDate = new Date(refYear, refMonth - 1, refDay + daysUntilNextMonday);
  const nextMondayStr = [
    nextMondayDate.getFullYear(),
    String(nextMondayDate.getMonth() + 1).padStart(2, '0'),
    String(nextMondayDate.getDate()).padStart(2, '0')
  ].join('-');

  // Carry-over overdue bills (unpaid bills from earlier months)
  const carryOverdueBills = expenses.filter(b => isBillOverdueCarryOver(b));
  const totalCarryOverdue = carryOverdueBills.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  // All overdue bills relative to current reference date
  const allOverdueBills = expenses.filter(b => {
    if (b.isPaid) return false;
    const bParts = parseDateParts(b.dueDate);
    const rParts = parseDateParts(config?.referenceDate || getTodayDateStr());
    const bDate = new Date(bParts.year, bParts.month - 1, bParts.day);
    const rDate = new Date(rParts.year, rParts.month - 1, rParts.day);
    return bDate < rDate;
  });

  // Filter displayed expenses based on monthFilter, categoryFilter, and searchTerm
  const filteredList = expenses.filter(bill => {
    const bParts = parseDateParts(bill.dueDate);
    const isThisMonth = bParts.year === refYear && bParts.month === refMonth;
    const isPriorOverdue = isBillOverdueCarryOver(bill);

    // 1. Month / Special timeline filter
    if (monthFilter === 'current') {
      // Rule: Keep all overdue bills visible in the next/active month so they can be monitored and managed!
      if (!isThisMonth && !isPriorOverdue) return false;
    } else if (monthFilter === 'overdue-only') {
      const rParts = parseDateParts(config?.referenceDate || getTodayDateStr());
      const bDate = new Date(bParts.year, bParts.month - 1, bParts.day);
      const rDate = new Date(rParts.year, rParts.month - 1, rParts.day);
      if (bill.isPaid || bDate >= rDate) return false;
    } else if (monthFilter === 'next-monday') {
      if (bill.isPaid || bill.dueDate > nextMondayStr) return false;
    } else if (monthFilter === 'urgent-48h') {
      const prox = getProximityStatus(bill.dueDate);
      if (bill.isPaid || !prox || !prox.isDueSoon) return false;
    }

    // 2. Category filter
    if (categoryFilter !== 'all' && bill.category !== categoryFilter) {
      return false;
    }

    // 3. Search query match
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const matchDesc = (bill.description || '').toLowerCase().includes(q);
      const matchSub = (bill.subDescription || '').toLowerCase().includes(q);
      const matchCat = (bill.category || '').toLowerCase().includes(q);
      const matchPriority = (bill.priority || '').toLowerCase().includes(q);
      const matchAmt = bill.amount.toString().includes(q);
      if (!matchDesc && !matchSub && !matchCat && !matchPriority && !matchAmt) {
        return false;
      }
    }

    return true;
  });

  const displayedExpenses = sortBillsByDueDate(filteredList, dueDateSortOrder);

  // Count of bills due in <= 48h
  const urgent48hBills = expenses.filter(b => {
    if (b.isPaid) return false;
    const prox = getProximityStatus(b.dueDate);
    return prox && prox.isDueSoon;
  });

  return (
    <div>
      {/* Header & Add Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
            Expenses & Invoices Ledger
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            Comprehensive schedule of business commitments, household costs, and supplier payments
          </p>
        </div>
        <button className="bt-btn-primary" onClick={handleOpenAdd}>
          <FaPlus /> Add Expense / Invoice
        </button>
      </div>

      {/* Action confirmation notification */}
      {notificationMessage && (
        <div style={{
          backgroundColor: '#ecfdf5',
          border: '1px solid #a7f3d0',
          color: '#065f46',
          padding: '10px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '0.88rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)'
        }}>
          <FaCheck style={{ color: '#10b981' }} />
          <span>{notificationMessage}</span>
        </div>
      )}

      {/* Search & Category Filter Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '14px'
      }}>
        {/* Instant Search Bar */}
        <div className="bt-search-container">
          <FaSearch className="bt-search-icon" />
          <input
            type="text"
            className="bt-search-input"
            placeholder="Search bills, vendor, category, or amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="bt-search-clear"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>

        {/* Category Filter Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FaFilter size={10} /> Category:
          </span>
          {['all', 'Business', 'Household', 'Others'].map((cat) => (
            <button
              key={cat}
              style={{
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: categoryFilter === cat ? '1px solid #4f46e5' : '1px solid #cbd5e1',
                backgroundColor: categoryFilter === cat ? '#4f46e5' : '#ffffff',
                color: categoryFilter === cat ? '#ffffff' : '#475569'
              }}
              onClick={() => setCategoryFilter(cat)}
            >
              {cat === 'all' ? 'All Categories' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Month Navigation & Timeline Filter Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '10px 16px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        {/* Month Switcher (Advances on 1st day of month) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(-1)}
            title="Go to previous month"
          >
            <FaChevronLeft size={10} /> Prev Month
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 8px' }}>
            <FaCalendarAlt style={{ color: '#2563eb' }} />
            <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{currentMonthLabel}</strong>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>(Ref: {config.referenceDate})</span>
          </div>

          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(1)}
            title="Advance to 1st day of next month (repeated bills will reflect)"
          >
            Next Month <FaChevronRight size={10} />
          </button>

          {config.referenceDate !== getTodayDateStr() && (
            <button
              className="bt-btn-secondary"
              style={{ padding: '6px 10px', fontSize: '0.8rem', color: '#059669', borderColor: '#a7f3d0', fontWeight: 600 }}
              onClick={handleReturnToToday}
              title="Return to today's real calendar date"
            >
              Today
            </button>
          )}
        </div>

          {/* Timeline Filter Selectors */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Timeline:</span>
            <button
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: monthFilter === 'current' ? '1px solid #2563eb' : '1px solid #cbd5e1',
                backgroundColor: monthFilter === 'current' ? '#2563eb' : '#ffffff',
                color: monthFilter === 'current' ? '#ffffff' : '#475569'
              }}
              onClick={() => setMonthFilter('current')}
              title={`Show ${currentMonthLabel} bills plus any unpaid overdue bills carried over`}
            >
              {currentMonthLabel.split(' ')[0]} + Overdue ({expenses.filter(b => {
                const bParts = parseDateParts(b.dueDate);
                return (bParts.year === refYear && bParts.month === refMonth) || isBillOverdueCarryOver(b);
              }).length})
            </button>

            {/* Overdue Filter Button */}
            <button
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                border: monthFilter === 'overdue-only' ? '1px solid #dc2626' : (allOverdueBills.length > 0 ? '1px solid #fca5a5' : '1px solid #cbd5e1'),
                backgroundColor: monthFilter === 'overdue-only' ? '#dc2626' : (allOverdueBills.length > 0 ? '#fef2f2' : '#ffffff'),
                color: monthFilter === 'overdue-only' ? '#ffffff' : (allOverdueBills.length > 0 ? '#b91c1c' : '#64748b'),
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onClick={() => setMonthFilter(monthFilter === 'overdue-only' ? 'current' : 'overdue-only')}
              title="Show all unpaid overdue bills that require action"
            >
              <FaExclamationCircle size={11} /> Overdue Only ({allOverdueBills.length})
            </button>

            {/* 48-Hour Proximity Warning Filter */}
            <button
              className={urgent48hBills.length > 0 && monthFilter !== 'urgent-48h' ? 'bt-pulse-urgent' : ''}
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                border: monthFilter === 'urgent-48h' ? '1px solid #dc2626' : (urgent48hBills.length > 0 ? '1px solid #f87171' : '1px solid #cbd5e1'),
                backgroundColor: monthFilter === 'urgent-48h' ? '#dc2626' : (urgent48hBills.length > 0 ? '#fef2f2' : '#ffffff'),
                color: monthFilter === 'urgent-48h' ? '#ffffff' : (urgent48hBills.length > 0 ? '#b91c1c' : '#64748b'),
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onClick={() => setMonthFilter('urgent-48h')}
              title="Filter to unpaid bills due today, tomorrow, or within 48 hours"
            >
              <FaBolt size={10} /> Due &le; 48h ({urgent48hBills.length})
            </button>

            <button
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: monthFilter === 'next-monday' ? '1px solid #ea580c' : '1px solid #cbd5e1',
                backgroundColor: monthFilter === 'next-monday' ? '#ea580c' : '#ffffff',
                color: monthFilter === 'next-monday' ? '#ffffff' : '#ea580c'
              }}
              onClick={() => setMonthFilter('next-monday')}
              title="Show all unpaid bills due on or before next Monday"
            >
              Due Next Monday ({expenses.filter(b => !b.isPaid && b.dueDate <= nextMondayStr).length})
            </button>

            <button
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: monthFilter === 'all' ? '1px solid #2563eb' : '1px solid #cbd5e1',
                backgroundColor: monthFilter === 'all' ? '#2563eb' : '#ffffff',
                color: monthFilter === 'all' ? '#ffffff' : '#475569'
              }}
              onClick={() => setMonthFilter('all')}
            >
              All Records ({expenses.length})
            </button>

            {(searchTerm || categoryFilter !== 'all' || monthFilter !== 'current') && (
              <button
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: '#f1f5f9',
                  border: '1px dashed #94a3b8',
                  color: '#64748b'
                }}
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('all');
                  setMonthFilter('current');
                }}
                title="Reset all active filters"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Overdue Carry-Over Notification Banner */}
        {carryOverdueBills.length > 0 && monthFilter !== 'overdue-only' && (
          <div style={{
            backgroundColor: '#fff1f2',
            border: '1px solid #fecdd3',
            borderRadius: '12px',
            padding: '12px 18px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: '0 2px 6px rgba(225, 29, 72, 0.06)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaExclamationTriangle style={{ color: '#e11d48', fontSize: '18px', flexShrink: 0 }} />
              <div>
                <strong style={{ color: '#9f1239', fontSize: '0.92rem' }}>
                  {carryOverdueBills.length} Overdue Bills Carried Over ({formatMonthYear(getYearMonth(carryOverdueBills[0]?.dueDate))} & Earlier):
                </strong>
                <span style={{ color: '#be123c', fontSize: '0.86rem', marginLeft: '6px' }}>
                  ${totalCarryOverdue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in unpaid obligations remains active and manageable in this month.
                </span>
              </div>
            </div>

            <button
              className="bt-btn-secondary"
              style={{
                fontSize: '0.78rem',
                padding: '5px 12px',
                borderColor: '#fda4af',
                backgroundColor: '#ffffff',
                color: '#be123c',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onClick={() => setMonthFilter('overdue-only')}
            >
              <FaExclamationCircle size={11} /> Filter Overdue Only
            </button>
          </div>
        )}

      {/* Spreadsheet Table */}
      <div className="bt-table-container">
        <table className="bt-table">
          <thead>
            <tr className="bt-table-header">
              <th style={{ width: '60px', textAlign: 'center' }}>PAID?</th>
              <th>BILL DESCRIPTION</th>
              <th>CATEGORY</th>
              <th>PRIORITY LEVEL</th>
              <th 
                style={{ 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  backgroundColor: '#eff6ff', 
                  color: '#1d4ed8' 
                }}
                onClick={() => setDueDateSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                title={`Bills sorted by Due Date (${dueDateSortOrder === 'asc' ? 'Earliest First (Ascending)' : 'Latest First (Descending)'}). Click to reverse.`}
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span>DUE DATE</span>
                  {dueDateSortOrder === 'asc' ? (
                    <FaSortAmountDown size={12} style={{ color: '#2563eb' }} title="Ascending: Earliest to Latest" />
                  ) : (
                    <FaSortAmountUp size={12} style={{ color: '#2563eb' }} title="Descending: Latest to Earliest" />
                  )}
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.85 }}>
                    ({dueDateSortOrder === 'asc' ? 'Earliest' : 'Latest'})
                  </span>
                </div>
              </th>
              <th style={{ textAlign: 'right' }}>AMOUNT ($)</th>
              <th style={{ textAlign: 'center' }}>3-WK PLAN</th>
              <th style={{ textAlign: 'center' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {displayedExpenses.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#334155' }}>
                    No expenses or bills scheduled for {currentMonthLabel}.
                  </div>
                  <div style={{ fontSize: '0.85rem', marginTop: '6px' }}>
                    Bills configured to repeat next month will automatically reflect starting on the 1st day of that month.
                  </div>
                </td>
              </tr>
            ) : (
              displayedExpenses.map((bill) => {
                const status = getDueDateStatus(bill);
                const isImportant = bill.priority.startsWith('Important');
                const isPriorOverdue = isBillOverdueCarryOver(bill);

                return (
                  <tr key={bill.id} className={`bt-table-row ${bill.isPaid ? 'row-paid' : ''} ${isPriorOverdue ? 'bt-row-overdue-carry' : ''}`}>
                    {/* Paid checkbox */}
                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      <div
                        className={`bt-checkbox ${bill.isPaid ? 'checked' : ''}`}
                        onClick={() => handleTogglePaid(bill.id)}
                        title={bill.isPaid ? 'Mark as unpaid' : 'Mark as paid'}
                        style={{ margin: '0 auto' }}
                      >
                        {bill.isPaid && <FaCheck size={11} />}
                      </div>
                    </td>

                    {/* Description & Subtext */}
                    <td className="bt-table-cell">
                      <div className="bt-title-cell" style={{ textDecoration: bill.isPaid ? 'line-through' : 'none' }}>
                        {bill.description}
                        {isPriorOverdue && !bill.isPaid && (
                          <span style={{
                            marginLeft: '6px',
                            fontSize: '0.68rem',
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fca5a5',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <FaExclamationCircle size={9} /> Overdue from {formatShortMonth(getYearMonth(bill.dueDate))}
                          </span>
                        )}
                        {bill.isInvoice && (
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '0.7rem',
                            background: '#f1f5f9',
                            color: '#475569',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            fontWeight: 600
                          }}>
                            <FaFileInvoiceDollar style={{ marginRight: '3px' }} />
                            Invoice
                          </span>
                        )}
                        {bill.repeatNextMonth && (
                          <span style={{
                            marginLeft: '6px',
                            fontSize: '0.68rem',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#2563eb',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <FaSyncAlt size={8} /> Recurring
                          </span>
                        )}
                      </div>
                      {bill.subDescription && (
                        <div className="bt-subtext">{bill.subDescription}</div>
                      )}
                      {bill.isExtended && bill.extensionReason && (
                        <div style={{ fontSize: '0.74rem', color: '#d97706', marginTop: '2px' }}>
                          * Extension: {bill.extensionReason}
                        </div>
                      )}
                    </td>

                    {/* Category */}
                    <td className="bt-table-cell">
                      <span className={`bt-badge ${getCategoryBadgeClass(bill.category)}`}>
                        {bill.category}
                      </span>
                    </td>

                    {/* Priority Level */}
                    <td className="bt-table-cell">
                      <span className={`bt-badge ${getPriorityBadgeClass(bill.priority)}`}>
                        {bill.priority}
                      </span>
                    </td>

                    {/* Due Date with Color Coding & Proximity Badge */}
                    <td className="bt-table-cell">
                      <div className="bt-date-pill" style={{ flexWrap: 'wrap', gap: '4px' }}>
                        <span style={{ color: status.color, fontWeight: 700 }}>{bill.dueDate}</span>
                        {status.status === 'overdue' && (
                          <span className="bt-badge-overdue">OVERDUE</span>
                        )}
                        {status.status === 'extended' && (
                          <span className="bt-badge-extended">EXTENDED</span>
                        )}
                        {!bill.isPaid && (() => {
                          const prox = getProximityStatus(bill.dueDate);
                          if (!prox || !prox.isDueSoon) return null;
                          return (
                            <span
                              style={{
                                backgroundColor: prox.level === 'urgent' ? '#fef2f2' : '#fffbeb',
                                color: prox.level === 'urgent' ? '#dc2626' : '#d97706',
                                border: `1px solid ${prox.level === 'urgent' ? '#fca5a5' : '#fde68a'}`,
                                fontSize: '0.68rem',
                                fontWeight: 800,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px'
                              }}
                            >
                              <FaBolt size={8} /> {prox.label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>

                    {/* Amount */}
                    <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.98rem' }}>
                      ${bill.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>

                    {/* 3-Week Tuesday Plan */}
                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      {bill.hasThreeWeekPlan ? (
                        <button
                          className="bt-badge"
                          style={{
                            background: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            cursor: 'pointer',
                            fontWeight: 700
                          }}
                          onClick={() => onOpenTuesdayPlan(bill)}
                        >
                          <FaCalendarAlt /> Yes (3-Wk)
                        </button>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>-</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        {/* Extension button for important bills */}
                        {isImportant && !bill.isPaid && (
                          <button
                            className="bt-icon-btn"
                            title="Request Date Extension"
                            style={{ color: '#d97706' }}
                            onClick={() => {
                              setExtensionModalBill(bill);
                              setExtensionDate(bill.dueDate);
                              setExtensionReason('');
                            }}
                          >
                            <FaClock size={14} />
                          </button>
                        )}

                        {/* Duplicate Button */}
                        <button
                          className="bt-icon-btn"
                          title="Duplicate this Bill"
                          style={{ color: '#0284c7' }}
                          onClick={() => handleDuplicateBill(bill)}
                        >
                          <FaCopy size={13} />
                        </button>

                        <button
                          className="bt-icon-btn"
                          title="Edit Bill"
                          onClick={() => handleOpenEdit(bill)}
                        >
                          <FaEdit size={14} />
                        </button>

                        <button
                          className="bt-icon-btn danger"
                          title="Delete Bill"
                          onClick={() => handleDelete(bill.id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Bill Modal */}
      {isAddModalOpen && (
        <div className="bt-modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="bt-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bt-modal-header">
              <h3>{editingBill ? 'Edit Expense / Invoice' : 'Add New Expense or Bill'}</h3>
              <button className="bt-icon-btn" onClick={() => setIsAddModalOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveBill}>
              <div className="bt-modal-body">
                {dateChangeBlockedMessage && (
                  <div style={{
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#b45309',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FaLock /> {dateChangeBlockedMessage}
                  </div>
                )}

                <div className="bt-form-group">
                  <label>Bill Description *</label>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="e.g. Commercial Office Lease & Workspace"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>

                <div className="bt-form-group">
                  <label>Subtitle / Supplier Details</label>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="e.g. HQ tenancy agreement or AWS cluster"
                    value={formData.subDescription}
                    onChange={(e) => setFormData({ ...formData, subDescription: e.target.value })}
                  />
                </div>

                <div className="bt-form-row">
                  <div className="bt-form-group">
                    <label>Category</label>
                    <select
                      className="bt-select"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="Business">Business</option>
                      <option value="Household">Household</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  <div className="bt-form-group">
                    <label>Priority Level *</label>
                    <select
                      className="bt-select"
                      value={formData.priority}
                      onChange={(e) => {
                        const nextPriority = e.target.value;
                        const isImp = nextPriority.startsWith('Important');
                        setFormData({
                          ...formData,
                          priority: nextPriority,
                          hasThreeWeekPlan: isImp ? true : formData.hasThreeWeekPlan
                        });
                      }}
                    >
                      <option value="Important (High Priority)">Important (High Priority)</option>
                      <option value="Average Important">Average Important</option>
                      <option value="Less Important">Less Important</option>
                    </select>
                  </div>
                </div>

                <div className="bt-form-row">
                  <div className="bt-form-group">
                    <label>
                      Due Date * {editingBill?.priority.startsWith('Important') && (
                        <span style={{ color: '#d97706', fontSize: '0.78rem' }}>(Locked: Request Extension)</span>
                      )}
                    </label>
                    <input
                      type="date"
                      className="bt-input"
                      value={formData.dueDate}
                      disabled={editingBill?.priority.startsWith('Important')}
                      onChange={(e) => handleDueDateInputChange(e.target.value)}
                      required
                    />
                  </div>

                  <div className="bt-form-group">
                    <label>Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bt-input"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.isInvoice}
                      onChange={(e) => setFormData({ ...formData, isInvoice: e.target.checked })}
                    />
                    Is this an official Invoice?
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.hasThreeWeekPlan}
                      onChange={(e) => setFormData({ ...formData, hasThreeWeekPlan: e.target.checked })}
                    />
                    Enable 3-Week Tuesday Plan
                  </label>
                </div>

                <div style={{
                  marginTop: '14px',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formData.repeatNextMonth}
                      onChange={(e) => setFormData({ ...formData, repeatNextMonth: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                    />
                    <span>🔁 Repeat Following Month (Recurring Monthly)</span>
                  </label>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px', paddingLeft: '24px', lineHeight: '1.4' }}>
                    Starting from the 1st day of the following month, this bill will automatically reflect again in the ledger. It will never appear before the new month starts.
                  </div>
                </div>
              </div>

              <div className="bt-modal-footer">
                <button
                  type="button"
                  className="bt-btn-secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="bt-btn-primary">
                  {editingBill ? 'Save Changes' : 'Add Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extension Request Modal */}
      {extensionModalBill && (
        <div className="bt-modal-backdrop" onClick={() => setExtensionModalBill(null)}>
          <div className="bt-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bt-modal-header">
              <h3>
                <FaClock style={{ color: '#d97706', marginRight: '8px' }} />
                Request Due Date Extension
              </h3>
              <button className="bt-icon-btn" onClick={() => setExtensionModalBill(null)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleRequestExtension}>
              <div className="bt-modal-body">
                <p style={{ fontSize: '0.9rem', color: '#475569', marginTop: 0 }}>
                  Rescheduling high-priority bills flags them as <strong>EXTENDED (Yellow)</strong> in your cashflow dashboard and updates your financial obligations calendar.
                </p>

                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '10px',
                  marginBottom: '18px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontWeight: 700 }}>{extensionModalBill.description}</div>
                  <div className="bt-subtext">Original Due Date: {extensionModalBill.dueDate}</div>
                  <div style={{ fontWeight: 800, marginTop: '4px' }}>
                    Amount: ${extensionModalBill.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="bt-form-group">
                  <label>New Requested Due Date *</label>
                  <input
                    type="date"
                    className="bt-input"
                    value={extensionDate}
                    onChange={(e) => setExtensionDate(e.target.value)}
                    required
                  />
                </div>

                <div className="bt-form-group">
                  <label>Extension Reason / Supplier Notes</label>
                  <textarea
                    className="bt-textarea"
                    rows="3"
                    placeholder="e.g. Agreement with vendor to push payment by 10 days"
                    value={extensionReason}
                    onChange={(e) => setExtensionReason(e.target.value)}
                  />
                </div>
              </div>

              <div className="bt-modal-footer">
                <button
                  type="button"
                  className="bt-btn-secondary"
                  onClick={() => setExtensionModalBill(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bt-btn-primary"
                  style={{ background: '#d97706' }}
                >
                  Confirm & Mark Yellow (Extended)
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesSheet;
